﻿import React, { useState, useEffect } from 'react';
import { CrudFunctions } from '../utilities/crud-functions';
import { WeightDataSpecifications } from '../models/weight-data-specifications';
import { BenchSheetWeightDatum } from '../models/bench-sheet-weight-datum';
import { Document } from '../models/document';
import { BenchSheetTechnologyType } from '../models/enums';
import { Decimal6Format } from '../templates/pw-formatting';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';

import DataGrid, {
    Column,
    Selection,
    Toolbar,
    Button as ColumnButton,
    Item as ToolbarItem,
    Paging,
    Editing,
    LoadPanel,
    Scrolling,
    Summary,
    TotalItem
} from 'devextreme-react/data-grid';

import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    benchSheetId: string;
    technologyId: BenchSheetTechnologyType;
    isReadOnly: boolean;
    datagridHeight?: string;
    title?: string;
}

export default function BenchSheetWeightData(props: Props) {
    const [weightDataSpecifications, setWeightDataSpecifications] =
        useState<WeightDataSpecifications>();

    const [weightData, setWeightData] =
        useState<BenchSheetWeightDatum[]>([]);

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId]);

    const percentChangeTitle = () => {
        if (props.technologyId === BenchSheetTechnologyType.LossOnDrying)
            return "% LOD";
        else if (props.technologyId === BenchSheetTechnologyType.ResidueOnEvaporation)
            return "% ROE";
        else
            return "% ROI";
    }

    const averagePercentChangeTitle = () => {
        if (props.technologyId === BenchSheetTechnologyType.LossOnDrying)
            return "Avg LOD%: {0}";
        else if (props.technologyId === BenchSheetTechnologyType.ResidueOnEvaporation)
            return "Avg ROE%: {0}";
        else
            return "Avg ROI%: {0}";
    }

    const resultWeightTitle = () => {
        if (props.technologyId === BenchSheetTechnologyType.LossOnDrying)
            return "Avg Weight Lost";
        else if (props.technologyId === BenchSheetTechnologyType.ResidueOnEvaporation)
            return "ROE Weight";
        else
            return "ROI Weight";
    }

    return (
        <div>
            <DataGrid
                height={props.datagridHeight ? props.datagridHeight : "20rem"}
                allowColumnResizing={true}
                wordWrapEnabled={true}
                columnResizingMode="widget"
                onRowInserting={onWeightDataRowInserting}
                onRowInserted={onWeightDataRowInserted}
                onRowUpdating={onWeightDataRowUpdating}
                onRowUpdated={onWeightDataRowUpdated}
                onRowRemoving={onWeightDataRowRemoving}
                dataSource={weightData ? weightData : []}
                rowAlternationEnabled={true}
                showRowLines={true}
                showBorders={true}>

                <Scrolling columnRenderingMode='virtual' />
                <Paging enabled={false} />
                <LoadPanel enabled={true} />
                <Selection mode='single' />

                <Column type="buttons" width="7rem">
                    <ColumnButton name="edit" />
                    <ColumnButton name="delete" />
                    <ColumnButton hint="audit information" icon="clock"
                        onClick={onWeightDataAuditButtonClicked} />
                    <ColumnButton hint="information" icon="info"
                        onClick={onWeightDataInformationButtonClicked} />
                </Column>

                <Column dataField='sampleName' width='4rem' dataType="string" />

                <Column dataField='containerWeight' caption='Vessel, g' format={Decimal6Format} width='6rem' dataType="number" />
                <Column dataField='sampleWeight' caption='Sample, g' format={Decimal6Format} width='6rem' dataType="number" />
                <Column dataField='weight1' caption="First Processing, g" format={Decimal6Format}
                    width='6rem' alignment="right" dataType="number" />
                <Column dataField='weight2' caption="Second Processing, g" format={Decimal6Format}
                    width='6rem' alignment="right" dataType="number" />
                <Column dataField='weight3' caption="Third Processing, g" format={Decimal6Format}
                    width='6rem' alignment="right" dataType="number" />
                <Column dataField='weight4' caption="Fourth Processing, g" format={Decimal6Format}
                    width='6rem' alignment="right" dataType="number" />
                <Column dataField='constantWeightChange' caption='Constant Weight %'
                    dataType="number" format="##0.00'%'" width='6rem' allowEditing={false} />
                <Column dataField='percentChange' caption={percentChangeTitle()}
                    dataType="number" format="###0.000'%'" width='6rem' allowEditing={false} />
                <Column />

                <Summary>
                    <TotalItem displayFormat={averagePercentChangeTitle()} column="percentChange" summaryType="avg"
                        valueFormat={Decimal6Format} alignment="right" />
                </Summary>

                <Toolbar>
                    <ToolbarItem location='before'>
                        <div className="pw-datagrid-toolbar-template">
                            <div className="pw-datagrid-toobar-title">Weight Data</div>
                        </div>
                    </ToolbarItem>
                    <ToolbarItem location="after" name="addRowButton" />
                </Toolbar>

                <Editing mode="row" newRowPosition="last"
                    useIcons={true}
                    allowAdding={true}
                    allowUpdating={true}
                    allowDeleting={true}>
                </Editing>

            </DataGrid>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog} />
                </DialogPopup>
            }

            <input type="file"
                id="fileInputElement"
                accept=".xlsx"
                className="file-picker-input"
                value=""
                onChange={onFileLoaderChange} />

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onWeightDataRowInserting(e: any) {
        e.data.benchSheetId = props.benchSheetId;
        e.cancel = insertBenchSheetWeightDatum(e.data).then((insertedRow) => {
            e.data = insertedRow;
        })
    }

    async function onWeightDataRowInserted(e: any) {
        await getWeightData();
    }

    function onWeightDataRowUpdating(e: any) {
        const updatedRow = { ...e.oldData, ...e.newData };
        e.cancel = updateBenchSheetWeightDatum(updatedRow).then(() => false);
    }

    async function onWeightDataRowUpdated(e: any) {
        await getWeightData();
    }

    function onWeightDataRowRemoving(e: any) {
        e.cancel = deleteBenchSheetWeightDatum(e.data.benchSheetWeightDataId)
            .then(() => false);
    }


    async function importExcelWeightData(weightDataFile: Document) {
        switch (props.technologyId) {
            case BenchSheetTechnologyType.LossOnDrying:
                return CrudFunctions.postData(`benchsheets/importlodweightdata/${props.benchSheetId}`, weightDataFile);
            case BenchSheetTechnologyType.ResidueOnEvaporation:
                return CrudFunctions.postData(`benchsheets/importroeweightdata/${props.benchSheetId}`, weightDataFile);
            case BenchSheetTechnologyType.ResidueOnIgnition:
                return CrudFunctions.postData(`benchsheets/importroiweightdata/${props.benchSheetId}`, weightDataFile);
        }
    }

    function getWeightDataFile() {
        const fileElem = document.getElementById("fileInputElement") as HTMLInputElement;

        if (fileElem) {
            fileElem.click();
        }
    }

    async function onFileLoaderChange(e: any) {
        let files = e.target.files;

        if (files && files.length > 0) {
            let file = files[0];

            let doc: Document = {
                name: file.name,
                kbSize: file.size,
                mimeTypeString: file.type,
                parentId: props.benchSheetId
            };

            var reader = new FileReader();

            reader.onload = (event: any) => readFile(doc, event)
                .then((doc) => {
                    importExcelWeightData(doc)
                        .then(() => {
                            //todo ADD TOAST

                            getWeightData();
                        })
                        .catch((res) => {
                            showMessage(res, ModalMessageType.Error, "Error Importing Weight Data");
                        });
                });

            //TODO: convert to -> reader.readAsArrayBuffer(file);

            reader.readAsBinaryString(file);
        }
    }

    async function readFile(doc: Document, event: any): Promise<any> {
        if (event && event.target && event.target.result) {
            let binaryString = event.target.result;
            let documentData = btoa(binaryString);

            doc.base64Blob = documentData;
            return doc;
        }
    }

    async function getWeightDataSpecifications() {
        await CrudFunctions.getData(`benchsheets/weightdatacalculationspecs/${props.technologyId}/${props.benchSheetId}`)
            .then(data => {
                setWeightDataSpecifications(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    async function getWeightData() {
        if (props.technologyId && props.benchSheetId)
            await CrudFunctions.getData(`benchsheets/benchsheetweightdata/${props.technologyId}/${props.benchSheetId}`)
                .then(data => {
                    //console.log("weight data:");
                    //console.log(data);
                    setWeightData(data);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Weight Data");
                });
    }

    async function insertBenchSheetWeightDatum(weightDatum: BenchSheetWeightDatum) {
        try {
            const insertedRow = await CrudFunctions.postData("benchsheets/benchsheetweightdatum", weightDatum);
            return insertedRow;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Inserting Weight Data");
            throw new Error(err?.response?.data?.message || err?.message || "Error inserting row");
        }
    }

    async function updateBenchSheetWeightDatum(weightDatum: BenchSheetWeightDatum) {
        try {
            await CrudFunctions.putData("benchsheets/benchsheetweightdatum", weightDatum);
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Updating Weight Data");
            throw new Error(err?.response?.data?.message || err?.message || "Error updating row");
        }
    }

    async function deleteBenchSheetWeightDatum(weightDatumId: string) {
        try {
            await CrudFunctions.deleteData(`benchsheets/benchsheetweightdatum/${weightDatumId}`);
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Deleting Weight Data");
            throw new Error(err?.response?.data?.message || err?.message || "Error deleting row");
        }
    }

    async function getAllData() {
        await Promise.all([
            getWeightDataSpecifications(),
            getWeightData()
        ])
    }


    function onWeightDataAuditButtonClicked(e: any) {

    }


    function onWeightDataInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const benchSheetWeightDatum = e.row.data as BenchSheetWeightDatum;

            if (benchSheetWeightDatum) {
                let itemInformation: ItemBasicInformation = {
                    rowPointer: benchSheetWeightDatum.benchSheetWeightDataId,
                    createTimestamp: benchSheetWeightDatum.createTimestamp,
                    lastModifiedTimestamp: benchSheetWeightDatum.modifyTimestamp,
                    createUser: benchSheetWeightDatum.createUser,
                    modifyUser: benchSheetWeightDatum.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    //show/hide
    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

}